import { useBundle } from '@amzn/react-arb-tools';
import {
  PREFERS_COLOR_SCHEME_ID,
  token,
} from '@amzn/stencil-design-tokens/js/web/utils';
import { ThemeMode } from '@amzn/stencil-react-components/context';
import { Card } from '@amzn/stencil-react-components/card';
import {
  H1,
  H2,
  H3,
  H4,
  Text,
  TextVariant,
} from '@amzn/stencil-react-components/text';
import {
  Col,
  Flex,
  GridContainer,
  GridItem,
  Row,
} from '@amzn/stencil-react-components/layout';
import {
  AMAZON_ACCESSIBLE_SERVICES_LINK,
  AMAZON_PRIME_LINK,
  AMAZON_PHARMACY_LINK,
  AMAZON_LITERARY_LINK,
  AMAZON_RESEARCH_AWARDS_LINK,
  AWS_EDUCATE_LINK,
  AWS_EDUCATION_EQUITY_LINK,
  AWS_RESTART_LINK,
  AWS_SKILL_BUILDER_LINK,
  AWS_SKILLS_CENTER_LINK,
  DELIVERY_SERVICE_PARTNER_LINK,
  DIRECTORS_ELEVATION_LINK,
  HIRING_OUR_HEROES_LINK,
  HOMEPAGE_MESSAGE_BUNDLE_NAME,
  AMAZON_VOLUNTEERING_LINK,
  ONE_MEDICAL_LINK,
  PRIVACY_NOTICE_LINK,
  RISE_HOME_CONSTRUCTION_LINK,
  SNOWBALL_EXPRESS_LINK,
  SUPPLIER_CONNECT_LINK,
} from '../constants';
import { Link } from '@amzn/stencil-react-components/link';
import { t } from '../helpers';
import SignUpSection from './SignUpSection';
import {
  PropsWithChildren,
  ReactElement,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from 'react';
import {
  Button,
  ButtonIconPosition,
  ButtonVariant,
} from '@amzn/stencil-react-components/button';
import { Chip, ChipSize } from '@amzn/stencil-react-components/chip';
import { IconArrowRight } from '@amzn/stencil-react-icons';
import eventsCardImage1 from '../images/eventsCardImage1.png';
import eventsCardImage2 from '../images/eventsCardImage2.png';
import eventsCardImage3 from '../images/eventsCardImage3.png';
import thriveImage from '../images/thriveImage.png';
import spotlightImage from '../images/spotlightImage.jpg';
import howItWorksImage from '../images/howItWorksImage.png';
import { useBreakpoints } from '@amzn/stencil-react-components/responsive';
import { trackClick } from 'src/analytics/clickTracking';
import { recordRumEvent } from 'src/analytics/rum';
import {
  HOMEPAGE_PAGE_GROUP,
  PAGE_LOAD_EVENT,
} from 'src/constants/MonitoringConstants';
import {
  headingId,
  navigateToSection,
  openReference,
} from 'src/utils/redirectUtils';
import { getPartner } from 'src/utils/partner';
import { highlightedText } from 'src/utils/textUtils';
import { useLocation } from 'react-router-dom';

/**
 * Required since the nav bar is sticky, matches Stencil's SECONDARY_NAV_HEIGHT
 */
const STICKY_NAV_HEIGHT = '4rem';

const PADDING_300LR_700TB = {
  left: 'dimensions.spacing.300',
  right: 'dimensions.spacing.300',
  top: 'dimensions.spacing.700',
  bottom: 'dimensions.spacing.700',
};

const PADDING_200LR = {
  left: 'dimensions.spacing.200',
  right: 'dimensions.spacing.200',
};

const PADDING_700LR = {
  left: 'dimensions.spacing.700',
  right: 'dimensions.spacing.700',
};

const PAGE_WIDTH = 1440;

const PageSectionCol = ({
  id,
  background,
  children,
  center,
  isDarkSurface,
}: PropsWithChildren<{
  id: string;
  background: string;
  center?: boolean | undefined;
  /** Scopes the section to Stencil's dark tokens; set when background is a dark surface. */
  isDarkSurface?: boolean | undefined;
}>) => {
  const { matches } = useBreakpoints();

  return (
    <Col
      id={id}
      {...(isDarkSurface && { [PREFERS_COLOR_SCHEME_ID]: ThemeMode.Dark })}
      alignItems="center"
      padding={PADDING_300LR_700TB}
      backgroundColor={background}
      style={{ scrollMarginTop: STICKY_NAV_HEIGHT }}
    >
      <Col
        maxWidth={PAGE_WIDTH}
        width={'100%'}
        padding={matches.s ? PADDING_200LR : PADDING_700LR}
        margin={{ left: 'auto', right: 'auto' }}
        alignItems={center ? 'center' : undefined}
      >
        {children}
      </Col>
    </Col>
  );
};

const PageSection = ({
  id,
  pillar,
  background,
  bundle,
  children,
  center,
}: PropsWithChildren<{
  id: string;
  background: string;
  bundle: any;
  center?: boolean | undefined;
  pillar?: boolean | undefined;
}>) => (
  <PageSectionCol id={id} background={background} center={center}>
    <>
      {pillar ? (
        <>
          <H2
            id={headingId(id)}
            tabIndex={-1}
            color="color.text.accent-on-bg-default"
            style={{
              fontSize: '1.125rem',
              fontWeight: '700',
              lineHeight: '1.688',
              paddingBottom: '0.5rem',
            }}
          >
            {t(bundle, `${id}_title`)}
          </H2>
          <H3
            style={{
              fontSize: '2rem',
              fontWeight: '800',
              lineHeight: '1.1',
              paddingBottom: '1rem',
            }}
          >
            {t(bundle, `${id}_header`)}
          </H3>
        </>
      ) : (
        <H2
          id={headingId(id)}
          tabIndex={-1}
          style={{
            fontSize: '2rem',
            fontWeight: '800',
            lineHeight: '1.1',
            paddingBottom: '1rem',
          }}
        >
          {t(bundle, `${id}_header`)}
        </H2>
      )}
      <Text
        variant={TextVariant.BodyLG}
        style={{
          paddingBottom: '2.5rem',
        }}
      >
        {t(bundle, `${id}_description`)}
      </Text>
      {children}
    </>
  </PageSectionCol>
);

const InformationCardsGroup = ({
  cards,
  sectionId,
  showMore,
  showMoreAction,
  bundle,
}: {
  cards: ReactElement[];
  sectionId?: string | undefined;
  showMore?: boolean | undefined;
  showMoreAction?: () => void | undefined;
  bundle: any;
}) => (
  <Col width="100%" gridGap={'dimensions.spacing.400'}>
    <GridContainer>
      {(showMore ? cards : cards.slice(0, 3)).map((card, index) => {
        return (
          <GridItem lg={4} md={8} sm={4} key={index}>
            {card}
          </GridItem>
        );
      })}
    </GridContainer>
    {showMoreAction && (
      <Row>
        <Button
          dataTestId={`show-more-${sectionId}-button-data-test-id`}
          variant={ButtonVariant.Tertiary}
          iconPosition={ButtonIconPosition.Trailing}
          icon={<IconArrowRight />}
          onClick={() => {
            trackClick({
              elementId: showMore ? 'show-less' : 'show-more',
              elementType: 'button',
              pageGroup: HOMEPAGE_PAGE_GROUP,
            });
            showMoreAction();
          }}
        >
          {showMore ? t(bundle, 'show_less') : t(bundle, 'show_more')}
        </Button>
      </Row>
    )}
  </Col>
);

const InformationCard = ({
  title,
  content,
  buttonText,
  buttonIcon,
  buttonReference,
  bundle,
  chip,
  image,
  imageAlt,
  backgroundColor,
}: {
  title: string;
  content: string;
  buttonText: string;
  buttonReference: string;
  bundle: any;
  buttonIcon?: ReactNode;
  backgroundColor?: string | undefined;
  chip?: string | undefined;
  image?: string | undefined;
  imageAlt?: string | undefined;
}) => {
  const buttonAriaLabel = t(bundle, 'button_label_with_context', {
    label: t(bundle, buttonText),
    context: t(bundle, title),
  }) as string;

  return (
    <Card
      width="100%"
      height="100%"
      padding={0}
      overflow="hidden"
      backgroundColor={backgroundColor}
    >
      <Col width="100%" height="100%">
        {image && imageAlt && (
          <Flex flexDirection="row" alignItems="center" justifyContent="center">
            <img
              style={{
                width: '100%',
              }}
              src={image}
              alt={t(bundle, imageAlt)}
              aria-hidden="true"
              loading="lazy"
            />
          </Flex>
        )}
        <Col
          gridGap="dimensions.spacing.200"
          padding="dimensions.spacing.300"
          width={'100%'}
          flexGrow={1}
        >
          <H3 dataTestId={`${title}-data-test-id`}>{t(bundle, title)}</H3>
          {chip && <Chip size={ChipSize.ExtraSmall}>{t(bundle, chip)}</Chip>}
          <Col
            gridGap="dimensions.spacing.300"
            flexGrow={1}
            justifyContent="space-between"
          >
            <Text>{t(bundle, content)}</Text>
            <Row>
              <Button
                dataTestId={`${title}-button-data-test-id`}
                role="link"
                iconPosition={ButtonIconPosition.Trailing}
                icon={buttonIcon}
                aria-label={
                  buttonReference.startsWith('#')
                    ? buttonAriaLabel
                    : t(bundle, 'opens_in_new_tab', {
                        label: buttonAriaLabel,
                      })
                }
                onClick={() => {
                  trackClick({
                    elementId: title,
                    elementType: 'button',
                    pageGroup: HOMEPAGE_PAGE_GROUP,
                    metadata: { reference: buttonReference },
                  });
                  openReference(buttonReference);
                }}
              >
                {t(bundle, buttonText)}
              </Button>
            </Row>
          </Col>
        </Col>
      </Col>
    </Card>
  );
};

type SubmittedForm = 'card' | 'footer';

const useFocusOnRender = (shouldFocus: boolean) => {
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (shouldFocus) {
      headingRef.current?.focus();
    }
  }, [shouldFocus]);

  return headingRef;
};

const CardSuccessMessage = ({
  bundle,
  shouldFocus,
}: {
  bundle: any;
  shouldFocus: boolean;
}) => {
  const headingRef = useFocusOnRender(shouldFocus);

  return (
    <Col
      gridGap="dimensions.spacing.200"
      width="100%"
      padding="dimensions.spacing.400"
      dataTestId="sign-up-success-header-test-id"
    >
      <H4
        ref={headingRef}
        tabIndex={-1}
        fontSize={'1.375rem'}
        lineHeight={1.15}
      >
        {t(bundle, 'success_header_prefix_card')}{' '}
        <span style={{ color: token('color.text.accent-on-bg-default') }}>
          {t(bundle, 'success_header_in_this_together')}
        </span>{' '}
        {t(bundle, 'success_header_postfix_card')}
      </H4>
      <Text fontWeight="700">{t(bundle, 'success_subheader')}</Text>
      <Text>{t(bundle, 'success_description')}</Text>
      <Text fontWeight="700">{t(bundle, 'success_what_to_expect_header')}</Text>
      <Text>{t(bundle, 'success_what_to_expect_description')}</Text>
      <Text>{t(bundle, 'success_welcome_closing')}</Text>
    </Col>
  );
};

const FooterSuccessMessage = ({
  bundle,
  shouldFocus,
}: {
  bundle: any;
  shouldFocus: boolean;
}) => {
  const headingRef = useFocusOnRender(shouldFocus);

  return (
    <PageSectionCol
      id={'submit_interest_footer'}
      background={'color.surface.bg-default'}
      center={true}
    >
      <Col
        alignItems="center"
        gridGap="dimensions.spacing.400"
        width="100%"
        dataTestId="sign-up-success-footer-test-id"
      >
        <H2
          ref={headingRef}
          tabIndex={-1}
          style={{
            fontSize: '2rem',
            fontWeight: '800',
            lineHeight: '1.1',
            textAlign: 'center',
          }}
        >
          {t(bundle, 'success_header_prefix_footer')}{' '}
          <span style={{ color: token('color.text.accent-on-bg-default') }}>
            {t(bundle, 'success_header_in_this_together')}
          </span>
          {t(bundle, 'success_header_postfix_footer')}
        </H2>
        <Text fontWeight="700" textAlign="center">
          {t(bundle, 'success_subheader_footer')}
        </Text>
        <Col alignItems="center" gridGap="dimensions.spacing.100">
          <Text textAlign="center">
            {t(bundle, 'success_description_footer')}
          </Text>
          <Text textAlign="center">
            {t(bundle, 'success_description_stay_up_to_date_footer')}
          </Text>
        </Col>
        <Button
          dataTestId="success-explore-button-data-test-id"
          role="link"
          variant={ButtonVariant.Primary}
          onClick={() => {
            trackClick({
              elementId: 'success-explore',
              elementType: 'button',
              pageGroup: HOMEPAGE_PAGE_GROUP,
              metadata: { reference: '#upcoming_events' },
            });
            openReference('#upcoming_events');
          }}
        >
          {t(bundle, 'success_explore_button')}
        </Button>
      </Col>
    </PageSectionCol>
  );
};

export interface GrowThriveConnectPageProps {
  isClientNavigation?: boolean;
}

const GrowThriveConnectPage = ({
  isClientNavigation = false,
}: GrowThriveConnectPageProps) => {
  const [bundle] = useBundle(HOMEPAGE_MESSAGE_BUNDLE_NAME);
  const [expandedGrow, setExpandedGrow] = useState<boolean>(false);
  const [expandedThrive, setExpandedThrive] = useState<boolean>(false);
  const [expandedConnect, setExpandedConnect] = useState<boolean>(false);
  const [submittedForm, setSubmittedForm] = useState<SubmittedForm>();

  const { matches } = useBreakpoints();
  const { hash } = useLocation();
  const scrolledHash = useRef<string>();

  useEffect(() => {
    recordRumEvent(PAGE_LOAD_EVENT, {
      pageGroup: HOMEPAGE_PAGE_GROUP,
      partner: getPartner(),
    });
  }, []);

  useEffect(() => {
    if (hash && bundle && scrolledHash.current !== hash) {
      scrolledHash.current = hash;
      navigateToSection(hash, { focus: isClientNavigation });
    }
  }, [hash, bundle, isClientNavigation]);

  const PILLARS = [
    {
      id: 'grow',
      content: (
        <InformationCardsGroup
          cards={[
            {
              title: 'grow_aws_skill_builder',
              buttonText: 'grow_aws_skill_builder_button',
              buttonReference: AWS_SKILL_BUILDER_LINK,
            },
            {
              title: 'grow_aws_skill_center',
              buttonText: 'grow_aws_skill_center_button',
              buttonReference: AWS_SKILLS_CENTER_LINK,
            },
            {
              title: 'grow_aws_skill_restart',
              buttonText: 'grow_aws_skill_restart_button',
              buttonReference: AWS_RESTART_LINK,
            },
            {
              title: 'grow_aws_educate',
              buttonText: 'grow_aws_educate_button',
              buttonReference: AWS_EDUCATE_LINK,
            },
            {
              title: 'grow_aws_skill_kiro',
              buttonText: 'learn_more_button',
              buttonReference:
                'https://kiro.dev/docs/guides/learn-by-playing/?trk=ea3fd267-83f8-4377-afdc-896ae1abeb2f&sc_channel=el',
            },
            {
              title: 'grow_aws_skill_q',
              buttonText: 'learn_more_button',
              buttonReference:
                'https://aws.amazon.com/quick/?trk=b3c43c3b-0d1f-4528-8a65-1993c8c9a1fc&sc_channel=el',
            },
            {
              title: 'grow_hiring_our_heroes',
              buttonText: 'learn_more_button',
              buttonReference: HIRING_OUR_HEROES_LINK,
            },
            {
              title: 'grow_directors_elevation',
              buttonText: 'learn_more_button',
              buttonReference: DIRECTORS_ELEVATION_LINK,
            },
            {
              title: 'grow_amazon_research_awards',
              buttonText: 'grow_amazon_research_awards_button',
              buttonReference: AMAZON_RESEARCH_AWARDS_LINK,
            },
            {
              title: 'grow_delivery_service_partner',
              buttonText: 'learn_more_button',
              buttonReference: DELIVERY_SERVICE_PARTNER_LINK,
            },
          ].map(({ title, buttonText, buttonReference }) => (
            <InformationCard
              key={title}
              title={title}
              chip={`${title}_chip`}
              content={`${title}_content`}
              buttonText={buttonText}
              buttonIcon={<IconArrowRight />}
              bundle={bundle}
              buttonReference={buttonReference}
            />
          ))}
          sectionId="grow"
          showMore={expandedGrow}
          showMoreAction={() => setExpandedGrow(!expandedGrow)}
          bundle={bundle}
        />
      ),
      background: 'color.surface.bg-default',
    },
    {
      id: 'thrive',
      content: (
        <>
          <img
            src={thriveImage}
            style={{
              width: '100%',
              height: '400px',
              objectFit: 'cover',
              borderRadius: '15px',
              marginBottom: '2.5rem',
            }}
            aria-hidden="true"
          />
          <InformationCardsGroup
            cards={[
              {
                title: 'thrive_prime_access',
                buttonReference: AMAZON_PRIME_LINK,
              },
              {
                title: 'thrive_amazon_pharmacy',
                buttonReference: AMAZON_PHARMACY_LINK,
              },
              {
                title: 'thrive_amazon_one_medical',
                buttonReference: ONE_MEDICAL_LINK,
              },
              {
                title: 'thrive_amazon_accessible_services',
                buttonReference: AMAZON_ACCESSIBLE_SERVICES_LINK,
              },
              {
                title: 'thrive_rise_home_construction',
                buttonReference: RISE_HOME_CONSTRUCTION_LINK,
              },
            ].map(({ title, buttonReference }) => (
              <InformationCard
                key={title}
                title={title}
                content={`${title}_content`}
                buttonText={'learn_more_button'}
                buttonIcon={<IconArrowRight />}
                bundle={bundle}
                buttonReference={buttonReference}
              />
            ))}
            sectionId="thrive"
            showMore={expandedThrive}
            showMoreAction={() => setExpandedThrive(!expandedThrive)}
            bundle={bundle}
          />
        </>
      ),
      background: 'color.surface.bg-muted',
    },
    {
      id: 'connect',
      content: (
        <InformationCardsGroup
          cards={[
            {
              title: 'connect_amazon_supplier_connect',
              buttonReference: SUPPLIER_CONNECT_LINK,
            },
            {
              title: 'connect_amazon_literary',
              buttonReference: AMAZON_LITERARY_LINK,
            },
            {
              title: 'connect_amazon_global_volunteering',
              buttonReference: AMAZON_VOLUNTEERING_LINK,
            },
            {
              title: 'connect_aws_education_equity',
              buttonReference: AWS_EDUCATION_EQUITY_LINK,
            },
            {
              title: 'connect_snowball_express',
              buttonReference: SNOWBALL_EXPRESS_LINK,
            },
          ].map(({ title, buttonReference }) => (
            <InformationCard
              key={title}
              title={title}
              content={`${title}_content`}
              buttonText={'learn_more_button'}
              buttonIcon={<IconArrowRight />}
              bundle={bundle}
              buttonReference={buttonReference}
            />
          ))}
          sectionId="connect"
          showMore={expandedConnect}
          showMoreAction={() => setExpandedConnect(!expandedConnect)}
          bundle={bundle}
        />
      ),
      background: 'color.surface.bg-default',
    },
  ];

  function titleSection(): ReactNode {
    return (
      <PageSectionCol
        id={'grow-thrive-connect-header'}
        background="color.surface.dark-bg-static"
      >
        <GridContainer>
          <GridItem lg={6} md={8}>
            <Col
              {...{ [PREFERS_COLOR_SCHEME_ID]: ThemeMode.Dark }}
              gridGap="dimensions.spacing.400"
              justifyContent="center"
            >
              <H1
                id="gtc-header"
                color="color.text.primary"
                lineHeight={1}
                fontSize={matches.s ? '3.25rem' : '5.875rem'}
              >
                {t(bundle, 'page_title_prefix')}{' '}
                <span
                  style={{ color: token('color.text.accent-on-bg-default') }}
                >
                  {t(bundle, 'page_title_postfix')}{' '}
                </span>
              </H1>
              <H2 id="pathway-subheader" color="color.text.primary">
                {t(bundle, 'page_sub_title')}
              </H2>
              <Row>
                <Button
                  dataTestId="explore-button-data-test-id"
                  role="link"
                  onClick={() => {
                    trackClick({
                      elementId: 'explore',
                      elementType: 'button',
                      pageGroup: HOMEPAGE_PAGE_GROUP,
                      metadata: { reference: '#grow' },
                    });
                    openReference('#grow');
                  }}
                >
                  {t(bundle, 'explore_button')}
                </Button>
              </Row>
            </Col>
          </GridItem>
          <GridItem lg={6} md={8}>
            <Col gridGap="dimensions.spacing.200" width={'100%'}>
              <Card width={'100%'}>
                {submittedForm ? (
                  <CardSuccessMessage
                    bundle={bundle}
                    shouldFocus={submittedForm === 'card'}
                  />
                ) : (
                  <Col
                    gridGap="dimensions.spacing.400"
                    width={'100%'}
                    padding="dimensions.spacing.400"
                  >
                    <H3 fontSize={'1.375rem'} lineHeight={1.15}>
                      {highlightedText(
                        bundle,
                        'sign_up_to_join_header',
                        'sign_up_to_join_in_this_together_header',
                        'sign_up_to_join_postfix_header',
                      )}
                    </H3>
                    <Text>{t(bundle, 'submit_interest_description')}</Text>
                    <SignUpSection
                      footer={false}
                      onSuccess={() => setSubmittedForm('card')}
                    />
                    <Text
                      fontSize="text.size.50"
                      lineHeight="text.lineHeight.50"
                    >
                      {t(bundle, 'submit_interest_disclaimer', {
                        privacyLink: (chunks: ReactNode[]) => (
                          <Link
                            key="privacy-notice"
                            href={PRIVACY_NOTICE_LINK}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={t(bundle, 'opens_in_new_tab', {
                              label: t(
                                bundle,
                                'submit_interest_privacy_notice',
                              ),
                            })}
                            fontSize="text.size.50"
                            lineHeight="text.lineHeight.50"
                            onClick={() =>
                              trackClick({
                                elementId: 'privacy-notice',
                                elementType: 'link',
                                pageGroup: HOMEPAGE_PAGE_GROUP,
                                metadata: { href: PRIVACY_NOTICE_LINK },
                              })
                            }
                          >
                            {chunks}
                          </Link>
                        ),
                      })}
                    </Text>
                  </Col>
                )}
              </Card>
            </Col>
          </GridItem>
        </GridContainer>
      </PageSectionCol>
    );
  }

  return (
    <Col dataTestId="grow-thrive-connect-test-id">
      {titleSection()}

      <PageSection
        id="how_it_works"
        background="color.surface.bg-default"
        bundle={bundle}
      >
        <img
          src={howItWorksImage}
          style={{
            width: '100%',
            height: matches.s ? '224px' : '480px',
            objectFit: 'cover',
            borderRadius: '15px',
            marginBottom: '2.5rem',
          }}
          aria-hidden="true"
        />
        <InformationCardsGroup
          cards={[
            {
              title: 'how_it_works_grow',
              buttonReference: '#grow',
            },
            {
              title: 'how_it_works_thrive',
              buttonReference: '#thrive',
            },
            {
              title: 'how_it_works_connect',
              buttonReference: '#connect',
            },
          ].map(({ title, buttonReference }) => (
            <InformationCard
              key={title}
              title={title}
              content={`${title}_content`}
              buttonText={'learn_more_button'}
              bundle={bundle}
              buttonReference={buttonReference}
              backgroundColor="color.surface.bg-muted"
            />
          ))}
          bundle={bundle}
        />
      </PageSection>

      <PageSectionCol
        id="community-spotlight"
        background="color.surface.bg-muted"
      >
        <GridContainer>
          <GridItem lg={7} md={8} sm={4}>
            <Col gridGap={'dimensions.spacing.300'}>
              <H2
                color="color.text.accent-on-bg-default"
                style={{
                  fontSize: '1.125rem',
                  fontWeight: '700',
                }}
              >
                {t(bundle, 'community_spotlight_title')}
              </H2>
              <Text
                fontWeight="700"
                fontSize="2rem"
                lineHeight="1.25"
                color="color.text.primary"
              >
                {t(bundle, 'community_spotlight_quote')}
              </Text>
              <Text color="color.text.primary">
                {t(bundle, 'community_spotlight_quote_attribution')}
              </Text>
            </Col>
          </GridItem>
          <GridItem lg={5} md={8} sm={4}>
            <Col gridGap={'dimensions.spacing.100'} width={'100%'}>
              <img
                src={spotlightImage}
                style={{
                  width: '100%',
                  objectFit: 'cover',
                  height: 'auto',
                  borderRadius: '15px',
                }}
                aria-hidden="true"
              />
              <Text
                color="color.text.primary"
                fontSize="0.75rem"
                lineHeight="1.125"
              >
                {t(bundle, 'community_spotlight_image_footer')}
              </Text>
            </Col>
          </GridItem>
        </GridContainer>
      </PageSectionCol>

      {PILLARS.map((pillar) => {
        return (
          <PageSection
            key={pillar.id}
            pillar={true}
            id={pillar.id}
            background={pillar.background}
            bundle={bundle}
          >
            {pillar.content}
          </PageSection>
        );
      })}

      <PageSection
        id={'upcoming_events'}
        background={'color.surface.bg-default'}
        bundle={bundle}
      >
        <InformationCardsGroup
          cards={[
            {
              title: 'upcoming_events_demystifying_generative_ai',
              buttonReference:
                'https://educationprograms.skillbuilder.aws/skillscenters/course-details/CTQ94FGYC5?trk=a45dbf55-f05a-48ac-b5da-9f2cee16279b&sc_channel=el',
              image: eventsCardImage1,
              imageAlt: 'upcoming_events_image1_alt',
            },
            {
              title: 'upcoming_events_job_roles_in_the_cloud',
              buttonReference:
                'https://educationprograms.skillbuilder.aws/skillscenters/course-details/AYZ6CSXEAU?trk=bc765460-0a50-47c1-97a1-b3290d2bc3da&sc_channel=el',
              image: eventsCardImage2,
              imageAlt: 'upcoming_events_image2_alt',
            },
            {
              title: 'upcoming_events_aws_skill_center',
              buttonReference:
                'https://educationprograms.skillbuilder.aws/skillscenters/course-details/6B6AVB5B9Q?trk=6720e239-e11c-4475-8b57-a94167626334&sc_channel=el',
              image: eventsCardImage3,
              imageAlt: 'upcoming_events_image3_alt',
            },
          ].map(({ title, buttonReference, image, imageAlt }) => (
            <InformationCard
              key={title}
              title={title}
              content={`${title}_content`}
              buttonText={'register_now_button'}
              buttonIcon={<IconArrowRight />}
              bundle={bundle}
              buttonReference={buttonReference}
              image={image}
              imageAlt={imageAlt}
            />
          ))}
          bundle={bundle}
        />
      </PageSection>

      {submittedForm ? (
        <FooterSuccessMessage
          bundle={bundle}
          shouldFocus={submittedForm === 'footer'}
        />
      ) : (
        <PageSection
          id={'submit_interest_footer'}
          background={'color.surface.bg-default'}
          bundle={bundle}
          center={true}
        >
          <Row
            justifyContent="center"
            gridGap="dimensions.spacing.200"
            width={'100%'}
          >
            <SignUpSection
              footer={true}
              onSuccess={() => setSubmittedForm('footer')}
            />
          </Row>
        </PageSection>
      )}
    </Col>
  );
};

export default GrowThriveConnectPage;
